import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const TTS_URL = `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak_stream_new`;

interface TTSState {
  status: "idle" | "loading" | "playing" | "paused" | "error";
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  error: string | null;
  currentId: string | null;
}

const initialState: TTSState = {
  status: "idle",
  isPlaying: false,
  isLoading: false,
  isPaused: false,
  error: null,
  currentId: null,
};

let audioContext: AudioContext | null = null;
let isStopped = false;
// Track the active reader for cancellation
let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;


let gainNode: GainNode | null = null;
let scheduledSources: AudioBufferSourceNode[] = [];
// --------------------------------------------
// 🔥 STREAM TTS (WAV / PCM)
// --------------------------------------------
export const speakText = createAsyncThunk(
  "tts/speak",
  async (
    { text, voice = "default.wav", id }: { text: string; voice?: string; id: string },
    { dispatch }
  ) => {
    try {
      console.log("speakText called");
      isStopped = false;
      // Stop any previous playback before starting new
      scheduledSources.forEach((src) => {
        try { src.stop(); } catch {}
      });
      scheduledSources = [];

      //  Close any existing AudioContext before creating a new one
      if (audioContext) {
        await audioContext.close();
        audioContext = null;
      }

      audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);

      const response = await fetch(
        `${TTS_URL}?text=${encodeURIComponent(text)}&voice=${voice}`,
        { method: "POST" }
      );

      if (!response.body) {
        throw new Error("No response body");
      }

      // Store reader reference for cancellation via stopTTS
      activeReader = response.body.getReader();
      const reader = activeReader;
      const sampleRate = 24000;
      let nextPlayTime = audioContext.currentTime;
      let leftover = new Uint8Array(0);
      let chunkCount = 0;

      // Track all scheduled sources so we know when the last one ends
      scheduledSources = [];

      dispatch(setStatus("playing"));

      while (true) {
        if (isStopped) break;

        const { done, value } = await reader.read();

        if (done) break;
        if (!value || value.byteLength === 0) continue;

        chunkCount++;
        console.log(`[TTS] chunk #${chunkCount} size: ${value.length}`);

        const combined = new Uint8Array(leftover.length + value.length);
        combined.set(leftover);
        combined.set(value, leftover.length);

        const usableLength = combined.length - (combined.length % 2);

        if (usableLength === 0) {
          leftover = combined;
          continue;
        }

        const pcmChunk = combined.slice(0, usableLength);
        leftover = combined.slice(usableLength);

        try {
          const int16 = new Int16Array(pcmChunk.buffer);
          const float32 = new Float32Array(int16.length);

          for (let i = 0; i < int16.length; i++) {
            float32[i] = int16[i] / 32767;
          }

          const buffer = audioContext.createBuffer(1, float32.length, sampleRate);
          buffer.copyToChannel(float32, 0);

          const source = audioContext.createBufferSource();
          source.buffer = buffer;

          // connect via gainNode instead of direct destination
          source.connect(gainNode!);

          const MIN_BUFFER = 0.15;
          if (nextPlayTime < audioContext.currentTime + MIN_BUFFER) {
            nextPlayTime = audioContext.currentTime + MIN_BUFFER;
          }

          source.start(nextPlayTime);
          nextPlayTime += buffer.duration;

          // track sources
          scheduledSources.push(source);
        } catch (e) {
          console.warn("[TTS] decode error:", e);
        }
      }

       //  Cleanup reader ref
       activeReader = null;

      //  Cancel the reader to close the HTTP stream connection on frontend side
      try {
        await reader.cancel();
        console.log("[TTS] Stream reader cancelled — connection closed");
      } catch (e) {
        console.warn("[TTS] reader.cancel() error:", e);
      }

      //  Wait for the last scheduled buffer to finish, then close AudioContext
      if (scheduledSources.length > 0 && !isStopped) {
        const lastSource = scheduledSources[scheduledSources.length - 1];

        await new Promise<void>((resolve) => {
          lastSource.onended = () => {
            console.log("[TTS] Last audio buffer finished playing");
            resolve();
          };
        });
      }

      //  Close AudioContext after all audio has played
      if (audioContext && !isStopped) {
        await audioContext.close();
        audioContext = null;
        console.log("[TTS] AudioContext closed after playback complete");
      }

      scheduledSources = [];
      gainNode = null;

      dispatch(setStatus("idle"));
    } catch (err: any) {
      //  Also close AudioContext on error
      if (audioContext) {
        await audioContext.close().catch(() => {});
        audioContext = null;
      }
      dispatch(setError(err.message));
    }
  }
);

// --------------------------------------------
// STOP
// --------------------------------------------
export const stopTTS = createAsyncThunk("tts/stop", async (_, { dispatch }) => {
  console.log("stopTTS called");
  isStopped = true;

  //  1. Stop all scheduled audio immediately
  scheduledSources.forEach((src) => {
      try {
        src.onended = null;
        src.stop(0);
      } catch {}
    });
  scheduledSources = [];

  //  2. Cancel streaming
  if (activeReader) {
    try {
      await activeReader.cancel().catch(() => {});
      console.log("[TTS] Active reader cancelled by stopTTS");
    } catch (e) {
      console.warn("[TTS] Error cancelling reader:", e);
    }
    activeReader = null;
  }

  //  3. Close AudioContext
  if (audioContext) {
    await audioContext.close().catch(() => {});
    audioContext = null;
  }

  //  4. Reset gainNode (important)
  gainNode = null;
  dispatch(setStatus("idle"));
});

export const pauseTTS = createAsyncThunk("tts/pause", async (_, { dispatch }) => {
  console.log("pauseTTS called");
  /*isStopped = true;

  scheduledSources.forEach((src) => {
    try { src.stop(); } catch {}
  });*/
  if (gainNode) {
    gainNode.gain.setValueAtTime(0, audioContext!.currentTime);
  }

  dispatch(setStatus("paused"));
});

export const resumeTTS = createAsyncThunk("tts/resume", async (_, { dispatch }) => {
  console.log("resumeTTS called");
  if (gainNode) {
    gainNode.gain.setValueAtTime(1, audioContext!.currentTime);
  }
  dispatch(setStatus("playing"));
});

// --------------------------------------------
// SLICE
// --------------------------------------------
const ttsSlice = createSlice({
  name: "tts",
  initialState,
  reducers: {
    setStatus(state, action) {
      state.status = action.payload;
      state.isPlaying = action.payload === "playing";
      state.isLoading = action.payload === "loading";
    },
    setError(state, action) {
      state.status = "error";
      state.error = action.payload;
      state.isPlaying = false;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(speakText.pending, (state, action) => {
        state.status = "loading";
        state.isLoading = true;
        state.error = null;
        state.currentId = action.meta.arg.id;
      })
      .addCase(speakText.fulfilled, (state) => {
        state.status = "idle";
        state.isPlaying = false;
        state.isLoading = false;
      })
      .addCase(speakText.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message || "Error";
        state.isPlaying = false;
        state.isLoading = false;
      })
      .addCase(stopTTS.fulfilled, (state) => {
        state.status = "idle";
        state.isPlaying = false;
        state.isLoading = false;
        state.currentId = null;
      });
  },
});

export const { setStatus, setError } = ttsSlice.actions;

export const selectTTSStatus = (state: any) => state.tts.status;
export const selectIsPlaying = (state: any) => state.tts.isPlaying;
export const selectIsLoading = (state: any) => state.tts.isLoading;
export const selectTTSError = (state: any) => state.tts.error;
export const selectCurrentTTSId = (state: any) => state.tts.currentId;

export default ttsSlice.reducer;